import React from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';

import { ArticleHeader } from '../components/ArticleHeader';
import { Footer } from '../components/new/Footer';
import { SEO } from '../components/seo';

import { Container } from '../components/Container';

import { Wrap } from '../components/new/Wrap';
import { PageWrapper } from '../components/new/wrappers';
import { BlogCardsComponent } from '../components/new/BlogCardsComponent';
import { H2 } from '../pages/offer';

const formatter = new Intl.DateTimeFormat('ru', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
});

export const formatDateToLocaleRu = inputDate => {
    if (inputDate) {
        return formatter
            .format(new Date(inputDate))
            .split('')
            .slice(0, -3)
            .join('');
    } else {
        return null;
    }
};

export const ContentWrapper = styled.div`
    max-width: 752px;
    padding: 0;
    margin-left: 112px;
    box-sizing: border-box;

    @media screen and (max-width: 760px) {
        margin-left: 0;
    }
`;

const ArticleHtmlText = styled.div`
    &.article-typography {
        font-family: 'Circe Web';
        font-size: 22px;
        line-height: 140%;
        color: #404040;

        a {
            color: #0b9cdb;

            &:hover {
                text-decoration: underline;
            }
        }

        img {
            max-width: 100%;
            height: auto;
            margin: 32px 0 48px;
        }

        p {
            margin: 0;

            & + p {
                margin-top: 32px;
            }

            & + ul,
            & + ol {
                margin-top: 16px;
            }
        }

        ul,
        ol {
            padding: 0 20px;
            box-sizing: border-box;

            li {
                margin-top: 0;
                margin-bottom: 0;
            }

            & + p {
                margin-top: 16px;
            }
        }

        ul {
            list-style: none;

            li::before {
                content: '•';
                color: inherit;
                display: inline-block;
                width: 20px;
                margin-left: -20px;
                text-align: center;
            }
        }

        h2,
        h3,
        h4 {
            color: #000;
            font-weight: 700;
            font-family: 'Futura PT', sans-serif;
        }

        h2 {
            font-size: 48px;
            line-height: 100%;
            margin: 48px 0 16px;

            @media screen and (max-width: 760px) {
                font-size: 28px;
                margin-top: 32px;
            }
        }

        h3 {
            font-size: 36px;
            line-height: 100%;
            margin: 32px 0 16px;

            @media screen and (max-width: 760px) {
                font-size: 28px;
                margin-top: 32px;
            }
        }

        table {
            max-width: 100%;
            table-layout: fixed;
            border-collapse: collapse;
            text-align: left;
            margin: 32px 0 24px;
            border-bottom: 2px solid #000;

            tr:last-child {
                td {
                    padding-bottom: 16px;
                }
            }

            th,
            td {
                font-family: 'Circe Web';
                font-size: 22px;
                line-height: 140%;

                @media screen and (max-width: 760px) {
                    font-size: 16px;
                    line-height: 140%;
                }
            }

            th {
                font-weight: 700;
                color: #000000;
                border-bottom: 2px solid;

                &:first-child {
                    min-width: 192px;
                    padding: 0 32px 0 0;
                    box-sizing: border-box;

                    @media screen and (max-width: 760px) {
                        min-width: 80px;
                        padding: 0 8px 0 0;
                    }
                }
            }

            td {
                vertical-align: top;
                color: #404040;
                padding: 16px 32px 8px 0;
                box-sizing: border-box;

                a {
                    color: #0b9cdb;

                    &:hover {
                        text-decoration: underline;
                    }
                }

                @media screen and (max-width: 760px) {
                    padding: 16px 8px 8px 0;
                }
            }
        }

        @media screen and (max-width: 760px) {
            font-size: 18px;
            line-height: 150%;
        }
    }

    @media screen and (max-width: 1200px) {
        margin: 0;
    }
`;

export const BlogPostTemplate = ({
    content,
    metadescription = 'Описание не задано. Будет применено описание по умолчанию.',
    title,
    metatitle = 'Заголовок не задан. Будет применен заголовок по умолчанию.',
    image,
    date,
    url,
}) => {
    return (
        <React.Fragment>
            <p>
                <b>URL:</b> https://kespa.ru/blog/<b>{url}</b>
            </p>
            <p>
                <b>Заголовок для поисковиков:</b> {metatitle}
            </p>
            <p>
                <b>Описание для поисковиков:</b> {metadescription}
            </p>
            <Wrap>
                <ArticleHeader title={title} date={date} imageSrc={image} />
                <Container>
                    <PageWrapper>
                        <ContentWrapper>
                            <ArticleHtmlText className="article-typography">
                                {content}
                            </ArticleHtmlText>
                        </ContentWrapper>
                    </PageWrapper>
                </Container>
            </Wrap>
        </React.Fragment>
    );
};

export const pageQuery = graphql`
    query BlogPostById($id: String!, $nextThreePostId: [String]) {
        markdownRemark(id: { eq: $id }) {
            id
            html
            cloudImage {
                fluid(
                    transformations: ["ar_2.2", "c_fill", "g_auto", "f_auto"]
                ) {
                    src
                }
            }
            frontmatter {
                title
                date
                metatitle
                metadescription
                isnew
            }
        }
        nextPosts: allMarkdownRemark(
            sort: { fields: frontmatter___date, order: DESC }
            filter: { id: { in: $nextThreePostId } }
        ) {
            nodes {
                cloudImage {
                    fluid(
                        transformations: [
                            "ar_5:2"
                            "c_fill"
                            "g_auto"
                            "f_auto"
                        ]
                    ) {
                        ...CloudinaryAssetFluid
                    }
                }
                frontmatter {
                    url
                    title
                    date
                }
                id
            }
        }
    }
`;

export default function BlogPost({ data }) {
    const post = data.markdownRemark;
    const { frontmatter } = post;
    const { nodes: nextPosts } = data.nextPosts;
    const date = formatDateToLocaleRu(frontmatter.date);
    return (
        <Wrap>
            <SEO
                title={frontmatter.metatitle || frontmatter.title}
                description={frontmatter.metadescription}
                isBlog
            />

            <ArticleHeader
                title={frontmatter.title}
                date={date}
                imageSrc={post.cloudImage.fluid.src}
            />

            <Container>
                <PageWrapper>
                    <ContentWrapper>
                        <ArticleHtmlText
                            className="article-typography"
                            dangerouslySetInnerHTML={{ __html: post.html }}
                        />
                    </ContentWrapper>
                </PageWrapper>
                {nextPosts && (
                    <>
                        <H2>Вам может быть интересно</H2>
                        <BlogCardsComponent postsArray={nextPosts} />
                    </>
                )}
            </Container>

            <Footer />
        </Wrap>
    );
}
